import React, { useState, useCallback, useMemo } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

const FontLoader = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap');
  body {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: ${p => p.theme.bg};
  }
`;
import EmailStep from './EmailStep';
import OtpStep from './shared/OtpStep';
import ConsentStep from './ConsentStep';
import TezLabLogo from '../../shared/TezLabLogo';
import SideAppLogo, { SideAppFullLogo } from '../../shared/SideAppLogo';
import buildThemeFromBranding from '../../shared/brandingTheme';

const STEPS = { EMAIL: 'email', OTP: 'otp', CONSENT: 'consent', COMPLETE: 'complete', ERROR: 'error' };

const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.pageBg};
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  padding-bottom: calc(24px + 10vh);
  font-family: ${p => p.theme.fontFamily};
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.div`
  margin-bottom: 14px;
`;

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
`;

const Card = styled.div`
  width: 100%;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radiusLg};
  padding: 40px;

  @media (max-width: 640px) {
    max-width: 100%;
    padding: 24px;
  }
`;

const PoweredBy = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${p => p.theme.textTertiary};
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.7;

  svg { opacity: 0.9; }
`;

const AppName = styled.div`
  color: ${p => p.theme.text};
  font-size: 18px;
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 6px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: ${p => p.theme.textSecondary};
  font-size: 15px;
  line-height: 24px;
`;

const ErrorHeading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  margin-bottom: 12px;
  text-align: center;
`;

// Known MCP client icons — PNG images served from /images/oauth-clients/
var CLIENT_ICONS = {
  claude: '/images/oauth-clients/claude.png',
  cursor: '/images/oauth-clients/cursor.png',
  chatgpt: '/images/oauth-clients/chatgpt.png',
  vscode: '/images/oauth-clients/vscode.png',
  windsurf: '/images/oauth-clients/windsurf.png',
  cline: '/images/oauth-clients/cline.png',
  zed: '/images/oauth-clients/zed.png'
};

export default function OAuthApp(props) {
  const {
    branding,
    clientName,
    appName,
    clientIconKey,
    scope,
    scopePermissions,
    optionalScopes,
    csrfToken,
    authenticated,
    userName,
    userEmail,
    error: initialError
  } = props;

  const theme = useMemo(function() { return buildThemeFromBranding(branding); }, [branding]);

  var clientIconSrc = clientIconKey ? CLIENT_ICONS[clientIconKey] : null;

  const initialStep = initialError ? STEPS.ERROR
    : authenticated ? STEPS.CONSENT
    : STEPS.EMAIL;

  const [step, setStep] = useState(initialStep);
  const [email, setEmail] = useState(userEmail || '');
  const [publicKey, setPublicKey] = useState(null);
  const [resendAt, setResendAt] = useState(null);
  const [authUserName, setAuthUserName] = useState(userName || '');
  const [authUserEmail, setAuthUserEmail] = useState(userEmail || '');
  const [errorMessage] = useState(initialError || null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
  }), [csrfToken]);

  const onEmailSubmitted = useCallback((data) => {
    setPublicKey(data.public_key);
    setResendAt(data.resend_allowed_at);
    setStep(STEPS.OTP);
  }, []);

  const onVerify = useCallback(async (encryptedOtp) => {
    const response = await fetch('/oauth/web/verify_otp', {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ email: email, otp: encryptedOtp }),
    });
    const data = await response.json();
    if (response.ok && data.authenticated) {
      setAuthUserName(data.user_name);
      setAuthUserEmail(data.user_email);
      setStep(STEPS.CONSENT);
      return;
    }
    throw new Error(data.error_description || 'Invalid code. Please try again.');
  }, [email, headers]);

  const onResend = useCallback(async () => {
    const response = await fetch('/oauth/web/send_otp', {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ email: email }),
    });
    const data = await response.json();
    if (response.ok) {
      setPublicKey(data.public_key);
      setResendAt(data.resend_allowed_at);
      return;
    }
    throw new Error(data.error_description || 'Could not resend code.');
  }, [email, headers]);

  const renderStep = () => {
    switch (step) {
      case STEPS.EMAIL:
        return (
          <EmailStep
            email={email}
            setEmail={setEmail}
            headers={headers}
            onSuccess={onEmailSubmitted}
          />
        );
      case STEPS.OTP:
        return (
          <OtpStep
            email={email}
            publicKey={publicKey}
            resendAt={resendAt}
            onVerify={onVerify}
            onResend={onResend}
          />
        );
      case STEPS.CONSENT:
        return (
          <ConsentStep
            clientName={clientName}
            appName={appName}
            clientIconSrc={clientIconSrc}
            scope={scope}
            permissions={scopePermissions}
            optionalScopes={optionalScopes}
            userEmail={authUserEmail}
            userName={authUserName}
            csrfToken={csrfToken}
            onApproved={function() { setStep(STEPS.COMPLETE); }}
          />
        );
      case STEPS.COMPLETE:
        return (
          <div>
            <ErrorHeading>You're all set!</ErrorHeading>
            <ErrorMessage>You can close this window and return to {clientName}.</ErrorMessage>
          </div>
        );
      case STEPS.ERROR:
        return (
          <div>
            <ErrorHeading>Something went wrong</ErrorHeading>
            <ErrorMessage>{errorMessage}</ErrorMessage>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <FontLoader />
      <Page>
        <Container>
          <LogoBlock>
            {branding && branding.fullLogoKey ? (
              <Logo><SideAppFullLogo fullLogoKey={branding.fullLogoKey} /></Logo>
            ) : (
              <>
                <Logo>
                  {branding && (branding.logoImageUrl || branding.logoKey)
                    ? <SideAppLogo logoImageUrl={branding.logoImageUrl} logoKey={branding.logoKey} alt={branding.appName} />
                    : <TezLabLogo />}
                </Logo>
                {branding && branding.showPoweredBy && branding.appName && (
                  <AppName>{branding.appName}</AppName>
                )}
                {branding && branding.showPoweredBy && (
                  <PoweredBy>
                    <span>powered by</span>
                    <TezLabLogo height={22} fill={theme.textTertiary} />
                  </PoweredBy>
                )}
              </>
            )}
          </LogoBlock>
          <Card>
            {renderStep()}
          </Card>
        </Container>
      </Page>
    </ThemeProvider>
  );
}
